import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, DataTable, LoadingSpinner, useToastActions, SearchInput, useDataTableUrl } from '@airclou/ui';
import { Plus, Pencil, Trash2, Group } from 'lucide-react';
import { listGroups, deleteGroup } from '@/api/groups';
import type { CompanyGroup } from '@/api/groups';
import { CreateGroupModal } from './CreateGroupModal';
import { EditGroupModal } from './EditGroupModal';
import { DeleteGroupModal } from './DeleteGroupModal';
import { ErrorState } from '../state/ErrorState';
import { useTimezone } from '@airclou/shell/timezone'
import { formatDateInTz } from '@airclou/utils'
import { useAuth } from '../../contexts/AuthContext'
import { canManageOrg } from '../../navigation'
import { RoleUnknownNotice } from '../state/RoleUnknownNotice';

interface CompanyGroupsTabProps {
  companyId: string;
}

export function CompanyGroupsTab({ companyId }: CompanyGroupsTabProps) {
  const timezone = useTimezone()
  const { t } = useTranslation('dashboard');
  const { success, error: showError } = useToastActions();
  const { user } = useAuth();

  // Group management was ungated on the API until 2026-09-13 and is now
  // `TENANT_ROLE_SETS.MANAGE`, so these buttons went from working for any member
  // to 403ing for anyone below admin. `canManageOrg` mirrors that role set; see
  // the same note in CompanyMembersTab. Rows stay readable either way — it is
  // only the writes that move.
  const canManage = canManageOrg(user?.orgRole ?? undefined);
  const [groups, setGroups] = useState<CompanyGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<CompanyGroup | null>(null);
  const [deletingGroup, setDeletingGroup] = useState<CompanyGroup | null>(null);
  // Sort and search live in the URL (same pattern as the audit log), so they
  // survive a reload, a shared link and back/forward.
  const { search: searchQuery, onSearchChange, sorting, onSortingChange } = useDataTableUrl();

  // Which search is the current one. `SearchInput` debounces, so a request per
  // keystroke this is not — but a slow answer can still be overtaken by a later
  // query's, and the rows now stay on screen under a dim overlay while one is
  // in flight, so adopting it would leave rows for a search the operator has
  // already moved past, with nothing on screen to say so.
  const loadSeq = useRef(0);

  const loadData = useCallback(async () => {
    const seq = ++loadSeq.current;
    try {
      setLoading(true);
      setError(null);
      const res = await listGroups(companyId, {
        search: searchQuery || undefined,
      });
      if (seq !== loadSeq.current) return;
      setGroups(res.groups);
    } catch (err: any) {
      if (seq !== loadSeq.current) return;
      setError(err.message || t('groups.failedToLoad'));
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [companyId, searchQuery]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDelete = async (group: CompanyGroup) => {
    try {
      await deleteGroup(companyId, group.id);
      success(t('groups.groupDeleted'));
      setDeletingGroup(null);
      loadData();
    } catch (err: any) {
      showError(err.message || t('groups.failedToDelete'));
    }
  };

  const columns: any[] = [
    {
      id: 'name',
      header: t('common.name'),
      size: 250,
      isFlexible: true,
      cell: (group: CompanyGroup) => (
        <div>
          <div className="font-medium text-foreground">{group.name}</div>
          {group.description && (
            <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-[300px]">
              {group.description}
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'members',
      header: t('common.members'),
      size: 100,
      cell: (group: CompanyGroup) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {group.member_count}
        </span>
      ),
    },
    {
      id: 'workspaces',
      header: t('common.workspaces'),
      size: 100,
      cell: (group: CompanyGroup) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {group.workspace_count}
        </span>
      ),
    },
    {
      id: 'created_at',
      header: t('common.created'),
      size: 100,
      accessorKey: 'created_at',
      enableSorting: true,
      cell: (group: CompanyGroup) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {formatDateInTz(group.created_at, timezone)}
        </span>
      ),
    },
    // The whole column goes, rather than rendering empty cells: a column
    // reserves its width whether or not it has anything in it, so leaving it in
    // place would give a read-only viewer a blank 100px gutter with a blank
    // header over it.
    ...(canManage ? [{
      id: 'actions',
      header: '',
      size: 100,
      cell: (group: CompanyGroup) => (
        <div className="flex items-center justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              setEditingGroup(group);
            }}
          >
            <Pencil className="w-3.5 h-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              setDeletingGroup(group);
            }}
            className="text-red-600 hover:text-red-700 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      ),
    }] : []),
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            {t('common.groups')}
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t('groups.organizeUsers')}
          </p>
        </div>
        {canManage && (
          <Button onClick={() => setIsCreateModalOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            {t('groups.createGroup')}
          </Button>
        )}
      </div>

      <div className="flex items-center justify-between">
        <div className="flex-1 mr-4 relative">
          <SearchInput
            placeholder={t('groups.searchGroups')}
            value={searchQuery}
            onChange={onSearchChange}
            className="w-full max-w-sm"
          />
        </div>
      </div>

      <RoleUnknownNotice />

      {error ? (
        <ErrorState title={t('groups.failedToLoad')} description={error} onRetry={loadData} />
      ) : groups.length === 0 && !loading ? (
        <div className="p-12 text-center">
          <Group className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">{t('groups.noGroupsYet')}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            {t('groups.createGroupsHelp')}
          </p>
          {canManage && (
            <Button onClick={() => setIsCreateModalOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              {t('groups.createGroup')}
            </Button>
          )}
        </div>
      ) : (
        <div className="relative">
          {/* A search refetch keeps the rows it already has and dims them; only
              the first load has nothing to show and gets the table spinner. */}
          {loading && groups.length > 0 && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 dark:bg-gray-900/50">
              <LoadingSpinner />
            </div>
          )}
          <DataTable
            data={groups}
            columns={columns}
            sorting={sorting}
            onSortingChange={onSortingChange}
            getRowHref={(group) => `/companies/${companyId}/groups/${group.id}`}
            isLoading={loading && groups.length === 0}
          />
        </div>
      )}

      {isCreateModalOpen && (
        <CreateGroupModal
          companyId={companyId}
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={() => {
            setIsCreateModalOpen(false);
            loadData();
          }}
        />
      )}

      {editingGroup && (
        <EditGroupModal
          companyId={companyId}
          group={editingGroup}
          isOpen={!!editingGroup}
          onClose={() => setEditingGroup(null)}
          onSuccess={() => {
            setEditingGroup(null);
            loadData();
          }}
        />
      )}

      {deletingGroup && (
        <DeleteGroupModal
          group={deletingGroup}
          isOpen={!!deletingGroup}
          onClose={() => setDeletingGroup(null)}
          onConfirm={() => handleDelete(deletingGroup)}
        />
      )}
    </div>
  );
}
